'use client';

import type { ReactNode } from 'react';

import { cn } from '@ps/ui/utils';
import { Sparkles } from 'lucide-react';

/**
 * Green-gradient "Wear with AI" CTA button — 1:1 with the legacy
 * `ListingImageCarouselAffiliateOverlay`'s overlay button (the `<Center.Touchable>`
 * wrapped in a `LinearGradient` with `Colors.FreshLime` palette).
 *
 * On the PDP, this is mounted as `floatingActions` on `ProductImageCarousel`
 * — pinned to the top-right of the hero, identical to RN's `position: absolute`
 * placement.
 *
 * Re-usable: also referenced from the product-actions-menu wiring, but rendered
 * there as a `DropdownMenuItem` (no gradient chip); this component is the
 * gallery-overlay form factor only.
 */
export function WearWithAiCta({
  onClick,
  className,
  iconSlot,
}: {
  onClick: () => void;
  className?: string;
  /** Override the leading icon (defaults to `lucide-react`'s Sparkles —
   *  closest visual to legacy's bespoke `GirlMagicIcon`). */
  iconSlot?: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Wear with AI"
      className={cn(
        'text-content-1 text-label inline-flex items-center gap-1 rounded px-2 py-1.5 font-medium shadow-md transition-opacity hover:opacity-90',
        // Green-tinted gradient matches legacy `LinearGradient`
        // `['#000000', '#4DBC7B']` with horizontal angle.
        'to-brand from-surface-2 bg-gradient-to-r',
        className,
      )}
    >
      {iconSlot ?? <Sparkles className="size-4 shrink-0" aria-hidden />}
      <span>Wear with AI</span>
    </button>
  );
}
